import type { ComponentType } from 'react'
import type { UserRole } from '@/entities/session/types'
import {
  CalculatorIcon,
  CalendarClockIcon,
  CalendarIcon,
  FlaskIcon,
  PackageIcon,
  PencilIcon,
  PersonStandingIcon,
  RssIcon,
  SparklesIcon,
} from '@/shared/ui/icons'
import { getAccountMenuMobileLinks } from '@/widgets/navigation/accountMenuItems'

type IconType = ComponentType<{ className?: string; size?: number }>

/** Linha de apoio que a folha calcula ao abrir (horas do mês, não lidas, estado do VIDaaS). */
export type MobileMenuSubtitle = 'plantoes-hours' | 'news-unread' | 'signature-status'

export type MobileMenuLink = {
  kind: 'link'
  label: string
  icon: IconType
  to: string
  state?: unknown
  subtitle?: MobileMenuSubtitle
}

export type MobileMenuAction = { kind: 'action'; label: string; icon: IconType; action: 'edit-home-layout' }

export type MobileMenuItem = MobileMenuLink | MobileMenuAction

export type MobileMenuGroup = { id: 'frequent' | 'tools' | 'account'; items: MobileMenuLink[] }

/**
 * Folha "Mais" em três grupos (sketch 010, tela 9a):
 * 1. as mais usadas fora das abas (Plantões, Chat IA, Notícias);
 * 2. ferramentas de uso esporádico (Agenda, Exames, Atlas, Estoque, Calculadora);
 * 3. conta (Assinatura digital, Configurações e perfil).
 * Plantões e Estoque seguem o mesmo gate de papel da SidebarNavigation (só doctor).
 */
export const getMobileMenuGroups = (
  role: UserRole,
  isSystemAdmin = false,
): MobileMenuGroup[] => {
  const isDoctor = role === 'doctor'
  const frequent: MobileMenuLink[] = [
    ...(isDoctor
      ? [{ kind: 'link' as const, label: 'Plantões', icon: CalendarClockIcon, to: '/plantoes', subtitle: 'plantoes-hours' as const }]
      : []),
    { kind: 'link', label: 'Chat IA', icon: SparklesIcon, to: '/chat-ia' },
    { kind: 'link', label: 'Notícias', icon: RssIcon, to: '/noticias', subtitle: 'news-unread' },
  ]
  const tools: MobileMenuLink[] = [
    { kind: 'link', label: 'Agenda', icon: CalendarIcon, to: '/calendar' },
    { kind: 'link', label: 'Exames', icon: FlaskIcon, to: '/last-results' },
    { kind: 'link', label: 'Atlas de anatomia', icon: PersonStandingIcon, to: '/anatomy' },
    ...(isDoctor ? [{ kind: 'link' as const, label: 'Estoque', icon: PackageIcon, to: '/inventory' }] : []),
    { kind: 'link', label: 'Calculadora de doses', icon: CalculatorIcon, to: '/calculator' },
  ]
  const account: MobileMenuLink[] = getAccountMenuMobileLinks({ role, isSystemAdmin }).map(
    (item) => ({
      kind: 'link' as const,
      label: item.label,
      icon: item.icon,
      to: item.to,
      subtitle:
        item.key === 'certificate'
          ? ('signature-status' as const)
          : undefined,
    }),
  )
  return [
    { id: 'frequent', items: frequent },
    { id: 'tools', items: tools },
    { id: 'account', items: account },
  ]
}

/** Ação do rodapé da folha, junto de "Sair". */
export const EDIT_HOME_LAYOUT_ITEM: MobileMenuAction = {
  kind: 'action',
  label: 'Editar layout',
  icon: PencilIcon,
  action: 'edit-home-layout',
}

/** Lista plana na ordem da folha (grupos e depois o rodapé). */
export const getMobileMenuItems = (role: UserRole, isSystemAdmin = false): MobileMenuItem[] => [
  ...getMobileMenuGroups(role, isSystemAdmin).flatMap((group) => group.items),
  EDIT_HOME_LAYOUT_ITEM,
]

const MONTH_FORMAT = new Intl.DateTimeFormat('pt-BR', { month: 'long' })

/** "32 h em outubro". */
export const formatPlantoesHoursSubtitle = (totalHours: number, now: Date): string =>
  `${Math.round(totalHours)} h em ${MONTH_FORMAT.format(now)}`

/** "4 não lidas", "1 não lida"; nada quando está tudo lido. */
export const formatNewsUnreadSubtitle = (count: number): string | null => {
  if (count <= 0) return null
  return count === 1 ? '1 não lida' : `${count} não lidas`
}

export const formatSignatureSubtitle = (connected: boolean): string =>
  connected ? 'VIDaaS conectado' : 'VIDaaS desconectado'

